import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { envelopesApi } from '@/api/envelopes.api';
import { organizationsApi } from '@/api/organizations.api';
import { EnvelopesSection } from '@/pages/dashboard/EnvelopesListPage';
import { VerifyEmailBanner } from '@/components/auth/VerifyEmailBanner';
import { AppShell } from '@/components/layout/AppShell';
import { InteractiveTour, useShouldShowTour } from '@/components/onboarding/InteractiveTour';
import { useAuthStore } from '@/stores/auth.store';

// Hero module tiles — condensed to the four review-oriented buckets.
const HERO_TILES: { icon: string; color: string; key: string; label: string }[] = [
  { icon: 'fa-user-clock', color: '#fbbf24', key: 'pending_mine', label: 'Pending my review' },
  { icon: 'fa-users', color: '#38bdf8', key: 'pending_others', label: 'Pending others review' },
  { icon: 'fa-pen', color: '#a78bfa', key: 'draft', label: 'Drafts' },
  { icon: 'fa-ban', color: '#fb7185', key: 'declined', label: 'Declined' },
];

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const [showTour, dismissTour] = useShouldShowTour();

  // Single fetch powers the hero counts.
  const { data: env } = useQuery({
    queryKey: ['envelopes', 'dashboard'],
    queryFn: () => envelopesApi.list({ perPage: 250 }),
  });

  // Declined envelopes are archived on decline, so they fall out of the active
  // fetch above — count them with a dedicated query so the Declined tile is
  // accurate even though they no longer appear in the register.
  const { data: declinedEnv } = useQuery({
    queryKey: ['envelopes', 'dashboard', 'declined'],
    queryFn: () => envelopesApi.list({ status: 'declined', perPage: 1 }),
  });

  const items = useMemo(() => env?.items ?? [], [env]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const it of items) c[it.status] = (c[it.status] ?? 0) + 1;
    return c;
  }, [items]);

  // Split active (sent / in_progress) envelopes into those awaiting the current
  // user's own action vs. those waiting on someone else.
  const myEmail = user?.email?.toLowerCase();
  const { pendingMine, pendingOthers } = useMemo(() => {
    let mine = 0;
    let others = 0;
    for (const e of items) {
      if (e.status !== 'sent' && e.status !== 'in_progress') continue;
      const rec = myEmail ? e.recipients.find((r) => r.email.toLowerCase() === myEmail) : undefined;
      const awaitingMe =
        !!rec && rec.status !== 'signed' && rec.status !== 'declined' && rec.status !== 'expired';
      if (awaitingMe) mine += 1;
      else others += 1;
    }
    return { pendingMine: mine, pendingOthers: others };
  }, [items, myEmail]);

  const tileValue = (key: string): number => {
    if (key === 'pending_mine') return pendingMine;
    if (key === 'pending_others') return pendingOthers;
    if (key === 'declined') return declinedEnv?.total ?? 0;
    return counts[key] ?? 0; // draft
  };

  return (
    <AppShell>
      {showTour && <InteractiveTour onClose={dismissTour} />}

      <div
        style={{
          margin: '-28px',
          minHeight: 'calc(100% + 56px)',
          background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)',
          padding: '32px 28px 80px 28px',
          boxSizing: 'border-box',
          fontFamily: "'Inter', 'Segoe UI', sans-serif",
          color: '#1e3a5f',
        }}
      >
        {/* ── Welcome Hero Card ── */}
        <div
          style={{
            background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 55%, #0c2350 100%)',
            borderRadius: 16,
            padding: '28px 32px',
            marginBottom: 28,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 32px rgba(59,130,246,0.18)',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(59,130,246,0.07) 1px, transparent 0)',
              backgroundSize: '28px 28px',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '-60%',
              right: '5%',
              width: 340,
              height: 340,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />

          <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            {/* Left — company info */}
            <div>
              <p style={{ color: 'rgba(147,197,253,0.7)', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', margin: '0 0 4px' }}>
                Welcome back
              </p>
              <h1
                style={{
                  fontSize: 30,
                  fontWeight: 800,
                  margin: '0 0 2px',
                  letterSpacing: '-0.5px',
                  background: 'linear-gradient(90deg, #ffffff 30%, #93c5fd 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {user?.firstName || user?.organization.name || 'Welcome'}
              </h1>
              {user?.organization.name && (
                <p style={{ color: 'rgba(147,197,253,0.6)', fontSize: 12, margin: '0 0 4px', fontWeight: 500 }}>
                  {user.organization.name}
                </p>
              )}
              <p style={{ color: 'rgba(148,163,184,0.75)', fontSize: 13, margin: '0 0 18px' }}>
                {new Date().toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['eIDAS', 'PKI', 'Audit Trail'].map((b) => (
                  <span
                    key={b}
                    style={{
                      padding: '3px 10px',
                      borderRadius: 99,
                      border: '1px solid rgba(59,130,246,0.4)',
                      color: '#93c5fd',
                      fontSize: 11,
                      fontWeight: 600,
                      background: 'rgba(59,130,246,0.1)',
                    }}
                  >
                    {b}
                  </span>
                ))}
              </div>
            </div>

            {/* Review buckets — compact chips sitting on the same row as the
               greeting (patron: "hepsini tek sırada, isim ve tarihle aynı
               çerçevede; kareler küçülsün"). */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {HERO_TILES.map((s) => (
                <div
                  key={s.key}
                  title={s.label}
                  style={{
                    background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 12,
                    padding: '9px 13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    // Equal width for every chip (sized to the longest label) so
                    // the row reads as a uniform strip, not staggered boxes.
                    width: 190,
                    flexShrink: 0,
                    boxSizing: 'border-box',
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 9,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `${s.color}22`,
                      flexShrink: 0,
                    }}
                  >
                    <i className={`fas ${s.icon}`} style={{ fontSize: 14, color: s.color }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, minWidth: 0 }}>
                    <span style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>
                      {tileValue(s.key)}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        color: 'rgba(203,213,225,0.85)',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {s.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {user && !user.isEmailVerified && (
          <div style={{ marginBottom: 20 }}>
            <VerifyEmailBanner />
          </div>
        )}

        {/* ── Documents (moved in from the former standalone list page) ── */}
        <div style={{ marginBottom: 28 }}>
          <EnvelopesSection />
        </div>

        {/* Reports — merged in from the former standalone Reports page. */}
        <ReportsSection />
      </div>
    </AppShell>
  );
}

// ── Reports section (merged from the former Reports page) ──────────
const REPORT_STATUS_COLORS: Record<string, string> = {
  draft: '#9CA3AF',
  sent: '#3B82F6',
  in_progress: '#F59E0B',
  completed: '#10B981',
  declined: '#EF4444',
  expired: '#6B7280',
  voided: '#6B7280',
};

function ReportsSection() {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'overview'],
    queryFn: () => organizationsApi.reportsOverview(),
  });

  if (isLoading || !data) {
    return (
      <div style={{ marginTop: 24 }}>
        <div className="card text-center py-10 text-muted">{t('common.loading')}</div>
      </div>
    );
  }

  const totalEnvelopes = data.byStatus.reduce((s, x) => s + x.count, 0);
  const completedCount = data.byStatus.find((s) => s.status === 'completed')?.count ?? 0;
  const completionRate = totalEnvelopes > 0 ? Math.round((completedCount / totalEnvelopes) * 100) : 0;
  const monthlyMax = Math.max(1, ...data.monthly.map((m) => Math.max(m.created, m.completed)));

  return (
    <div style={{ marginTop: 28 }}>
      <div className="mb-4">
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e3a5f', margin: 0 }}>{t('reports.title')}</h2>
        <p className="text-[12px] text-muted mt-0.5">
          {t('reports.subtitle')} · {t('reports.window_days', { days: data.windowDays })}
        </p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <ReportKpi label={t('reports.kpi_total')} value={totalEnvelopes.toLocaleString()} />
        <ReportKpi label={t('reports.kpi_completed')} value={completedCount.toLocaleString()} />
        <ReportKpi label={t('reports.kpi_completion_rate')} value={`${completionRate}%`} />
        <ReportKpi
          label={t('reports.avg_response_hours')}
          value={data.avgResponseHours == null ? '—' : formatReportHours(data.avgResponseHours)}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        {/* Status distribution */}
        <div className="card">
          <h3 className="text-base font-semibold text-gray-900 mb-3">{t('reports.status_distribution')}</h3>
          {data.byStatus.length === 0 ? (
            <div className="text-muted text-[13px] py-6 text-center">{t('reports.no_data')}</div>
          ) : (
            <div className="space-y-2.5">
              {data.byStatus
                .slice()
                .sort((a, b) => b.count - a.count)
                .map((s) => {
                  const pct = totalEnvelopes > 0 ? (s.count / totalEnvelopes) * 100 : 0;
                  return (
                    <div key={s.status}>
                      <div className="flex justify-between text-[12px] mb-1">
                        <span className="text-gray-700">{t(`envelope.status_${s.status}`)}</span>
                        <span className="text-muted">
                          <strong className="text-gray-900">{s.count}</strong> · {pct.toFixed(0)}%
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${pct}%`, background: REPORT_STATUS_COLORS[s.status] ?? '#6B7280' }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* Top creators */}
        <div className="card">
          <h3 className="text-base font-semibold text-gray-900 mb-3">{t('reports.top_creators')}</h3>
          {data.topCreators.length === 0 ? (
            <div className="text-muted text-[13px] py-6 text-center">{t('reports.no_creators_window')}</div>
          ) : (
            <ul className="space-y-2">
              {data.topCreators.map((c) => {
                const rate = c.envelopeCount > 0 ? Math.round((c.completedCount / c.envelopeCount) * 100) : 0;
                return (
                  <li key={c.id} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-[11px] font-semibold shrink-0">
                      {c.name[0]?.toUpperCase() ?? '·'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-gray-900 truncate">{c.name}</div>
                      <div className="text-[11px] text-muted truncate">{c.email}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[13px] font-semibold text-gray-900">{c.envelopeCount}</div>
                      <div className="text-[10px] text-muted">{rate}% {t('reports.done_short')}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Monthly created vs completed */}
      <div className="card">
        <h3 className="text-base font-semibold text-gray-900 mb-1">{t('reports.monthly_trend')}</h3>
        <p className="text-[12px] text-muted mb-4">{t('reports.monthly_caption')}</p>
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${data.monthly.length}, minmax(0, 1fr))` }}>
          {data.monthly.map((m) => (
            <div key={m.month} className="flex flex-col items-center gap-1">
              <div className="flex items-end gap-0.5 h-[120px] w-full justify-center">
                <div
                  title={`${t('reports.created_label')}: ${m.created}`}
                  className="w-2.5 rounded-t bg-primary/60"
                  style={{ height: `${(m.created / monthlyMax) * 100}%` }}
                />
                <div
                  title={`${t('reports.completed_label')}: ${m.completed}`}
                  className="w-2.5 rounded-t bg-success"
                  style={{ height: `${(m.completed / monthlyMax) * 100}%` }}
                />
              </div>
              <div className="text-[10px] text-muted whitespace-nowrap">{reportMonthLabel(m.month, i18n.language)}</div>
              <div className="text-[10px] text-gray-700 font-medium">{m.created}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-4 justify-center mt-3 text-[11px] text-muted">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded bg-primary/60" /> {t('reports.created_label')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded bg-success" /> {t('reports.completed_label')}
          </span>
        </div>
      </div>
    </div>
  );
}

function ReportKpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="card stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

function formatReportHours(h: number): string {
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

function reportMonthLabel(yyyymm: string, lang: string): string {
  const [y, m] = yyyymm.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString(lang.startsWith('az') ? 'az-AZ' : 'en-US', { month: 'short' });
}
